import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Scale, Globe, FileText, Users, Building2, Megaphone,
  Target, ArrowRight, ExternalLink, BookOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';

const policyAreas = [
  {
    title: "Youth Empowerment & Employment",
    description: "Advocating for policies that create opportunities for young people in education, skills development, and employment across all regions.",
    icon: Users,
    color: "#7BC4E2"
  },
  {
    title: "Climate Action & Environmental Justice",
    description: "Engaging in climate policy discussions at UN and EU levels, promoting sustainable development and environmental protection.",
    icon: Globe,
    color: "#4A7C59"
  },
  {
    title: "Gender Equality & Social Inclusion",
    description: "Championing policies that advance gender equality, women's rights, and inclusion of marginalized communities.",
    icon: Scale,
    color: "#E08B7A"
  },
  {
    title: "Poverty Alleviation & Economic Development",
    description: "Contributing to policy frameworks that address root causes of poverty and promote sustainable economic growth.",
    icon: Target,
    color: "#E5B75F"
  }
];

const engagementPlatforms = [
  {
    name: "United Nations ECOSOC",
    description: "OPAD participates in UN Economic and Social Council sessions, contributing to global development policy discussions.",
    type: "UN Platform"
  },
  {
    name: "European Commission",
    description: "Engagement with EU institutions on development cooperation, humanitarian aid, and Erasmus+ programmes.",
    type: "EU Platform"
  },
  {
    name: "African Union",
    description: "Partnership with AU agencies on youth development, climate action, and regional integration initiatives.",
    type: "Regional Platform"
  },
  {
    name: "Civil Society Networks",
    description: "Active membership in global CSO networks advocating for SDG implementation and accountability.",
    type: "CSO Network"
  }
];

const recentStatements = [
  {
    title: "Submission to UNEP on Zero Draft Ministerial Declaration of UNEA-7",
    date: "28 April 2025",
    forum: "UN Environment Assembly (UNEA-7)",
    featured: true,
    content: `The Organization for Poverty Alleviation and Development (OPAD) welcomes the opportunity to provide written input to the Zero Draft Ministerial Declaration of the seventh session of the United Nations Environment Assembly (UNEA-7), pursuant to the first intergovernmental consultations held on 15 April 2025.`,
    recommendations: [
      "Prioritize Climate Justice and Resilience Building",
      "Adopt Binding Commitments on Pollution Control",
      "Recognize the Leadership of Indigenous Peoples and Youth",
      "Mandate Evidence-Based Policy-Making",
      "Embed Robust Accountability Mechanisms",
      "Strengthen Multilateral Cooperation and Solidarity"
    ]
  },
  {
    title: "Statement on Youth Employment in Africa",
    date: "December 2025",
    forum: "UN General Assembly Side Event"
  },
  {
    title: "Position Paper on Climate Finance for Developing Nations",
    date: "November 2025",
    forum: "COP29 Civil Society Forum"
  },
  {
    title: "Submission on EU Development Cooperation Strategy",
    date: "October 2025",
    forum: "European Commission Consultation"
  },
  {
    title: "Joint Statement on Gender-Responsive Humanitarian Action",
    date: "September 2025",
    forum: "UN Human Rights Council"
  }
];

const policyPapers = [
  { title: "Framework for Inclusive Economic Growth", date: "January 2026" },
  { title: "Education Access in Developing Nations", date: "December 2025" },
  { title: "Climate Resilience and Poverty Reduction", date: "October 2025" }
];

const advocacyCampaigns = [
  { title: "End Hunger Initiative 2026", status: "Ongoing" },
  { title: "Education for All Campaign", status: "Ongoing" },
  { title: "Clean Water Access Project", status: "Ongoing" }
];

const positionPapers = [
  { title: "OPAD Position on Global Health Equity", date: "February 2026" },
  { title: "Stance on International Aid Reform", date: "January 2026" },
  { title: "Position on Youth Employment Initiatives", date: "December 2025" }
];

export default function PolicyEngagementPage() {
  return (
    <div data-testid="policy-engagement-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4A7C59] via-[#5A8C69] to-[#6A9C79]">
          <div className="absolute top-0 right-0 w-[500px] h-[450px] bg-white/15 rounded-bl-[250px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[350px] bg-[#E5B75F]/20 rounded-tr-[180px]" />
          <div className="absolute top-[30%] left-[15%] w-[150px] h-[150px] bg-[#7BC4E2]/15 rounded-full" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Scale className="w-16 h-16 text-white mx-auto mb-6" />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-white mb-6">
                Policy Engagement
              </h1>
              <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
                OPAD actively engages with international policy frameworks to advocate for sustainable development, 
                social justice, and inclusive growth aligned with the UN Sustainable Development Goals.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[400px] h-[350px] bg-[#7BC4E2]/10 rounded-br-[180px]" />
          <div className="absolute bottom-0 right-0 w-[350px] h-[300px] bg-[#E08B7A]/10 rounded-tl-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-4xl mx-auto bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-slate-100 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Megaphone className="w-12 h-12 text-[#E08B7A] mx-auto mb-4" />
            <h2 className="text-2xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Our Voice in Global Policy
            </h2>
            <p className="text-slate-600 leading-relaxed">
              As a civil society organization committed to transformative change, OPAD participates in policy 
              dialogues at the United Nations, European Union, African Union, and other multilateral platforms. 
              We bring grassroots perspectives to high-level discussions, ensuring that the voices of communities 
              we serve are heard in shaping development policies that affect their lives.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Policy Areas */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-[#FDF8F4]">
          <div className="absolute top-0 right-0 w-[500px] h-[450px] bg-[#4A7C59]/10 rounded-bl-[250px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[350px] bg-[#E5B75F]/10 rounded-tr-[180px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-[#4A7C59]/20 rounded-full px-4 py-2 mb-4">
              <Target className="w-5 h-5 text-[#4A7C59]" />
              <span className="text-[#4A7C59] text-sm font-semibold">Thematic Focus</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Policy Priority Areas
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our policy engagement focuses on key thematic areas aligned with the SDGs and our programmatic work.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {policyAreas.map((area, index) => (
              <motion.div
                key={area.title}
                className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-start gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${area.color}20` }}
                  >
                    <area.icon className="w-6 h-6" style={{ color: area.color }} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold font-['Manrope'] text-slate-900 mb-2">
                      {area.title}
                    </h3>
                    <p className="text-slate-600 text-sm">
                      {area.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Platforms */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[450px] h-[400px] bg-[#7BC4E2]/10 rounded-br-[200px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[350px] bg-[#E08B7A]/10 rounded-tl-[180px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-[#7BC4E2]/20 rounded-full px-4 py-2 mb-4">
              <Building2 className="w-5 h-5 text-[#7BC4E2]" />
              <span className="text-[#7BC4E2] text-sm font-semibold">Global Presence</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Engagement Platforms
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              OPAD engages with key international and regional institutions to influence policy outcomes.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {engagementPlatforms.map((platform, index) => (
              <motion.div
                key={platform.name}
                className="bg-white/90 backdrop-blur-sm p-5 rounded-2xl shadow-lg border border-slate-100 text-center"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <span className="inline-block bg-[#E5B75F]/20 text-[#E5B75F] text-xs font-semibold px-3 py-1 rounded-full mb-3">
                  {platform.type}
                </span>
                <h3 className="text-base font-bold font-['Manrope'] text-slate-900 mb-2">
                  {platform.name}
                </h3>
                <p className="text-slate-600 text-sm">
                  {platform.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* UNEA-7 Featured Statement */}
      <section className="relative section-padding overflow-hidden" data-testid="unea7-statement">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4A7C59]/5 via-white to-[#7BC4E2]/5">
          <div className="absolute top-0 left-0 w-[400px] h-[350px] bg-[#E5B75F]/10 rounded-br-[200px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div 
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-[#4A7C59]/20 rounded-full px-4 py-2 mb-4">
              <Globe className="w-5 h-5 text-[#4A7C59]" />
              <span className="text-[#4A7C59] text-sm font-semibold">UN Statements</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Official UN Forum Statements
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Official statements delivered at United Nations forums and assemblies addressing global poverty and sustainable development.
            </p>
          </motion.div>

          {/* Featured UNEA-7 Statement */}
          <motion.div
            className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-r from-[#4A7C59] to-[#5A8C69] p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full">
                  FEATURED
                </span>
                <span className="text-white/80 text-sm">28 April 2025</span>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white">
                Submission to UNEP on Zero Draft Ministerial Declaration of UNEA-7
              </h3>
            </div>
            <div className="p-6 md:p-8">
              <p className="text-slate-700 leading-relaxed mb-6">
                The Organization for Poverty Alleviation and Development (OPAD) welcomes the opportunity to provide 
                written input to the Zero Draft Ministerial Declaration of the seventh session of the United Nations 
                Environment Assembly (UNEA-7), pursuant to the first intergovernmental consultations held on 15 April 2025.
              </p>
              
              <div className="bg-[#4A7C59]/5 rounded-xl p-6 mb-6">
                <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#4A7C59]" />
                  Key Recommendations
                </h4>
                <ul className="grid md:grid-cols-2 gap-3">
                  {recentStatements[0].recommendations.map((rec, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="w-6 h-6 bg-[#4A7C59] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <span className="text-slate-700 text-sm">{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t">
                <span className="text-sm text-slate-500">UN Environment Assembly (UNEA-7) • Nairobi, Kenya</span>
                <Button variant="outline" size="sm" className="text-[#4A7C59] border-[#4A7C59] hover:bg-[#4A7C59] hover:text-white">
                  <FileText className="w-4 h-4 mr-2" />
                  Read Full Statement
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Policy Papers Section */}
      <section className="relative section-padding overflow-hidden bg-slate-50">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-[#7BC4E2]/20 rounded-full px-4 py-2 mb-4">
              <BookOpen className="w-5 h-5 text-[#7BC4E2]" />
              <span className="text-[#7BC4E2] text-sm font-semibold">Policy Papers</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Research & Analysis
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Research-based policy recommendations and analysis on poverty alleviation strategies and development frameworks.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {policyPapers.map((paper, index) => (
              <motion.div
                key={paper.title}
                className="bg-white rounded-xl shadow-lg border border-slate-100 p-6 hover:shadow-xl transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <FileText className="w-10 h-10 text-[#7BC4E2] mb-4" />
                <h3 className="font-bold text-slate-900 mb-2">{paper.title}</h3>
                <p className="text-sm text-slate-500">{paper.date}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Advocacy Campaigns & Position Papers */}
      <section className="relative section-padding overflow-hidden">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Advocacy Campaigns */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#E08B7A]/20 rounded-full flex items-center justify-center">
                  <Megaphone className="w-6 h-6 text-[#E08B7A]" />
                </div>
                <h3 className="text-2xl font-bold font-['Manrope'] text-slate-900">Advocacy Campaigns</h3>
              </div>
              <p className="text-slate-600 mb-6">
                Active campaigns promoting policy changes and awareness on critical issues affecting vulnerable communities worldwide.
              </p>
              <div className="space-y-4">
                {advocacyCampaigns.map((campaign, index) => (
                  <div key={campaign.title} className="bg-white rounded-xl shadow-lg border border-slate-100 p-4 flex items-center justify-between">
                    <span className="font-medium text-slate-900">{campaign.title}</span>
                    <span className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full">
                      {campaign.status}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Position Papers */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#E5B75F]/20 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-[#E5B75F]" />
                </div>
                <h3 className="text-2xl font-bold font-['Manrope'] text-slate-900">OPAD Position Papers</h3>
              </div>
              <p className="text-slate-600 mb-6">
                Official organizational positions on key development issues and policy recommendations for global stakeholders.
              </p>
              <div className="space-y-4">
                {positionPapers.map((paper, index) => (
                  <div key={paper.title} className="bg-white rounded-xl shadow-lg border border-slate-100 p-4 flex items-center justify-between">
                    <div>
                      <span className="font-medium text-slate-900">{paper.title}</span>
                      <p className="text-xs text-slate-500 mt-1">{paper.date}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="text-[#E5B75F]">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* International Recognition & Institutional Engagement */}
      <section className="relative section-padding overflow-hidden" data-testid="international-recognition">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 right-0 w-[450px] h-[400px] bg-[#4A7C59]/8 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[350px] bg-[#E5B75F]/10 rounded-tr-[180px]" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-[#4A7C59]/15 rounded-full px-4 py-2 mb-4">
              <Globe className="w-5 h-5 text-[#4A7C59]" />
              <span className="text-[#4A7C59] text-sm font-semibold">Global Standing</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">
              International Recognition & Institutional Engagement
            </h2>
            <p className="text-slate-600 max-w-3xl mx-auto">
              OPAD works in close collaboration with global institutions and multilateral frameworks to advance sustainable development, humanitarian response, and inclusive social progress worldwide — reflecting our commitment to transparency, accountability, and active participation in international policy dialogue.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <motion.div className="bg-white p-6 rounded-2xl shadow-md border-t-4 border-[#4A7C59]" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h3 className="font-bold text-slate-900 mb-3">Consultative Status</h3>
              <ul className="text-sm text-slate-600 space-y-2 list-disc pl-5">
                <li>UN Economic and Social Council (ECOSOC) — Consultative Status</li>
                <li>UN Framework Convention on Climate Change (UNFCCC) — Engagement in global climate processes</li>
              </ul>
            </motion.div>

            <motion.div className="bg-white p-6 rounded-2xl shadow-md border-t-4 border-[#7BC4E2]" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.05 }}>
              <h3 className="font-bold text-slate-900 mb-3">Memberships & Global Platforms</h3>
              <ul className="text-sm text-slate-600 space-y-2 list-disc pl-5">
                <li>UN Global Compact (UNGC) — Member</li>
                <li>Adaptation Fund — International Partner / Stakeholder</li>
                <li>Global Nutrition Cluster — Member</li>
              </ul>
            </motion.div>

            <motion.div className="bg-white p-6 rounded-2xl shadow-md border-t-4 border-[#E08B7A]" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <h3 className="font-bold text-slate-900 mb-3">Collaboration with UN Bodies</h3>
              <ul className="text-sm text-slate-600 space-y-1.5 list-disc pl-5">
                <li>OHCHR</li>
                <li>UNODC</li>
                <li>FAO</li>
                <li>UNDP</li>
                <li>UNHCR</li>
              </ul>
            </motion.div>

            <motion.div className="bg-gradient-to-br from-[#003399] to-[#0055cc] p-6 rounded-2xl shadow-md text-white" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">🇪🇺</span>
                <h3 className="font-bold">European Union Engagement</h3>
              </div>
              <p className="text-sm text-white/90 mb-3">
                Registered in the EU Transparency Register (ID: <strong>026269897821-30</strong>) — actively contributing to EU policy dialogue, stakeholder consultations, and development cooperation initiatives.
              </p>
              <a
                href="https://transparency-register.europa.eu/search-register-or-update/organisation-detail_en?id=026269897821-30"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-white/20 hover:bg-white/30 rounded-full px-3 py-1.5 transition"
              >
                View EU Profile <ExternalLink className="w-3 h-3" />
              </a>
            </motion.div>
          </div>

          <motion.div
            className="max-w-4xl mx-auto mt-10 bg-[#FDF8F4] rounded-2xl p-6 border border-slate-100"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="font-bold text-slate-900 mb-2 text-center">Strategic Positioning</h3>
            <ul className="text-sm text-slate-600 space-y-1.5 max-w-2xl mx-auto list-disc pl-5">
              <li>Presented on OPAD's website to strengthen institutional credibility.</li>
              <li>Integrated across project proposals and donor communications.</li>
              <li>Public access to the EU Transparency Register profile ensures openness and accountability.</li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Recent Statements */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-[#FDF8F4]">
          <div className="absolute top-0 right-0 w-[500px] h-[450px] bg-[#E08B7A]/10 rounded-bl-[250px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[350px] bg-[#7BC4E2]/10 rounded-tr-[180px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-[#E08B7A]/20 rounded-full px-4 py-2 mb-4">
              <FileText className="w-5 h-5 text-[#E08B7A]" />
              <span className="text-[#E08B7A] text-sm font-semibold">Publications</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Recent Statements & Submissions
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {recentStatements.map((statement, index) => (
              <motion.div
                key={statement.title}
                className="bg-white/90 backdrop-blur-sm p-5 rounded-xl shadow-lg border border-slate-100 flex items-center justify-between"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">{statement.title}</h3>
                  <div className="flex items-center gap-3 text-sm text-slate-500">
                    <span>{statement.date}</span>
                    <span>•</span>
                    <span>{statement.forum}</span>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="text-[#7BC4E2] hover:text-[#6AB3D1]">
                  <BookOpen className="w-4 h-4" />
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7BC4E2] via-[#8BCDE8] to-[#9BD6EE]">
          <div className="absolute top-0 right-0 w-[400px] h-[350px] bg-white/15 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-[#4A7C59]/20 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Globe className="w-16 h-16 text-white mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mb-4">
              Collaborate With Us
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
              Join OPAD in advocating for policies that create lasting impact for communities worldwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/partnership">
                <Button className="bg-white text-[#7BC4E2] hover:bg-slate-100 rounded-full px-8 py-6 font-semibold">
                  Partner With Us
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#7BC4E2] rounded-full px-8 py-6 font-semibold">
                  Contact Policy Team
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
